/**
 * Every numeric constant in the model.
 *
 * The rule, from the build spec and enforced by `npm run lint`: each constant carries
 * either a `SOURCE:` citation or an explicit `UNSOURCED — tunable` marker. Unsourced
 * constants are collected into `Tunables` and exposed as sliders in the interface,
 * because the honest response to a parameter nobody has measured is to let the user
 * move it and watch what happens — not to publish an invented figure with two decimal
 * places and hope no one asks.
 *
 * Each tunable is declared as its own `const` rather than inline in the defaults object
 * so that the lint rule can see, and check, every single one.
 */

import type { Tunables } from './types.ts';

/**
 * Bumped whenever a change alters the output of a run for an unchanged config+seed.
 * Share links carry it so an old link can say "this was produced by an older model"
 * rather than silently replaying as something else.
 */
export const ENGINE_VERSION = '0.1.0';

// ---------------------------------------------------------------------------
// Structural constants
// ---------------------------------------------------------------------------

/**
 * Resolution of the discretised Granovetter threshold distribution. 51 bins puts a
 * member's threshold on a 2-percentage-point grid, which is finer than any real
 * measurement of collective-action thresholds could justify.
 * SOURCE: Granovetter (1978) works over the cumulative threshold distribution; the bin
 * count is an implementation detail of representing it, not a modelling claim.
 */
export const THRESHOLD_BIN_COUNT = 51;

/**
 * Participation share above which a sector counts as "mobilised" for breadth purposes.
 * UNSOURCED — tunable. Fixed rather than exposed because it is a reporting definition,
 * not a mechanism: it changes what the UI calls a mobilised sector, never the dynamics.
 */
export const SECTOR_MOBILISED_AT = 0.15;

/**
 * Absolute participation floor for declaring a campaign collapsed.
 * UNSOURCED — tunable. Reporting definition, as above.
 */
export const CAMPAIGN_COLLAPSE_BELOW = 0.01;

/**
 * A campaign is also collapsed once it has fallen to this fraction of its own peak, even
 * if the absolute number is still non-trivial.
 *
 * The relative test matters more than the absolute one. Every campaign leaves a committed
 * rump who would strike alone in an empty street, and a model that waits for them to give
 * up reports a dead campaign as an ongoing stalemate for months. "Stalemate" should mean
 * both sides are still standing, not that a remnant is still out.
 * UNSOURCED — tunable. Reporting definition.
 */
export const COLLAPSE_PEAK_FRACTION = 0.15;

/**
 * Consecutive days below `CAMPAIGN_COLLAPSE_BELOW` before collapse is declared, so a
 * single bad day does not end an otherwise live campaign.
 * UNSOURCED — tunable.
 */
export const COLLAPSE_CONFIRMATION_DAYS = 7;

/**
 * Loyalty below which a pillar stops obeying and its function leaves the regime.
 * UNSOURCED — tunable. Reporting definition: it fixes what the word "defected" means,
 * not how fast loyalty moves.
 */
export const PILLAR_DEFECTS_BELOW = 0.2;

/**
 * Loyalty below which a pillar is publicly wavering — reported in the feed, because the
 * visible hesitation of an institution is itself a signal to everyone else.
 * UNSOURCED — tunable. Reporting definition.
 */
export const PILLAR_WAVERS_BELOW = 0.4;

/**
 * Share of pillar weight that must defect before the regime can no longer govern.
 * UNSOURCED — tunable. Reporting definition: it fixes the victory condition's threshold,
 * and is deliberately well below 1 — a state does not need to lose every institution to
 * stop functioning, it needs to lose enough of them at once.
 */
export const PILLAR_TIPPING_POINT = 0.5;

/**
 * Concession level at which the movement's demands count as met.
 * UNSOURCED — tunable. Reporting definition.
 */
export const DEMANDS_MET_AT = 0.95;

/**
 * Concession level above which a partial settlement is on the table.
 * UNSOURCED — tunable. Reporting definition.
 */
export const NEGOTIABLE_ABOVE = 0.4;

/**
 * Peak participation a campaign must have reached for its wind-down to count as a
 * negotiated settlement rather than a collapse that happened to coincide with an offer.
 * UNSOURCED — tunable. Reporting definition. Deliberately low: most real campaigns settle
 * at peaks in the low single digits, and requiring a mass mobilisation before we are
 * willing to call something a negotiation would hide the most common way these end.
 */
export const NEGOTIATION_MINIMUM_PEAK = 0.04;

/** How much repression the regime can add in a single day. UNSOURCED — tunable. */
export const REPRESSION_STEP = 0.12;

/**
 * How much of its demands the regime gives up in a single concession.
 * UNSOURCED — tunable. Kept small deliberately: at 0.12 a regime under sustained pressure
 * signs away its entire position inside nine days, which is not how governments behave and
 * flattens the difference between a pragmatic one and an intransigent one.
 */
export const CONCESSION_STEP = 0.06;

/**
 * Daily probability that maximum repression against a perfectly disciplined movement in a
 * fully free media environment produces outrage rather than compliance.
 * UNSOURCED — tunable. Martin (2007) establishes the mechanism and the conditions — a
 * visible victim, an audience that reads them as innocent, channels the regime does not
 * control — but no source gives a per-day rate, and inventing one with a decimal point
 * would be exactly the false precision this project refuses.
 */
export const BACKFIRE_BASE_PROBABILITY = 0.28;

/**
 * Discipline below which the campaign is treated as having escalated to violence.
 * UNSOURCED — tunable. Reporting definition.
 */
export const VIOLENT_ESCALATION_BELOW = 0.35;

/**
 * Consecutive days below that line before escalation becomes the run's outcome, so a
 * single bad night does not redefine a campaign.
 * UNSOURCED — tunable. Reporting definition.
 */
export const ESCALATION_CONFIRMATION_DAYS = 5;

/**
 * Share of essential-sector output that must be lost before the public feels a shortage.
 * Below this it is a staffing problem; above it, it is empty shelves.
 * UNSOURCED — tunable.
 */
export const ESSENTIAL_SHORTAGE_ONSET = 0.25;

/**
 * Terms of the Neumann series used to propagate stoppages downstream. Twelve is far past
 * convergence for coefficients of this size.
 * SOURCE: Leontief (1936); the series expansion of (I - A)^-1 for a matrix with spectral
 * radius below 1.
 */
export const LEONTIEF_TERMS = 12;

// ---------------------------------------------------------------------------
// Sourced constants
// ---------------------------------------------------------------------------

/**
 * Observed success rate of nonviolent campaigns in the NAVCO dataset.
 * SOURCE: Chenoweth & Stephan (2011), *Why Civil Resistance Works*, p. 7 — nonviolent
 * campaigns succeeded 53% of the time against 26% for violent campaigns, across 323
 * campaigns 1900-2006.
 */
export const NONVIOLENT_SUCCESS_RATE = 0.53;

/**
 * Observed success rate of violent campaigns in the same dataset. Used as the base rate
 * a campaign inherits if it escalates to armed struggle — escalation is modelled as a
 * *worse* bet, which is the finding.
 * SOURCE: Chenoweth & Stephan (2011), *Why Civil Resistance Works*, p. 7.
 */
export const VIOLENT_SUCCESS_RATE = 0.26;

/**
 * The "3.5% rule": no campaign in the dataset that achieved sustained active
 * participation above this share of the population failed.
 *
 * Deliberately NOT a win condition. It enters the model only as the reference scale
 * against which breadth-of-participation pressure on pillar loyalty is normalised — see
 * DECISIONS.md D004. Chenoweth's figure is a description of observed campaigns, not a
 * causal mechanism, and hard-coding it would make the simulator unfalsifiable.
 * SOURCE: Chenoweth & Stephan (2011); Chenoweth, "The '3.5% rule': How a small minority
 * can change the world" (2019).
 */
export const PARTICIPATION_REFERENCE_SCALE = 0.035;

/**
 * Movement violence above this share of actions is treated as a discipline failure, at
 * which point repression stops backfiring and pillar loyalty begins to recover.
 * SOURCE: Chenoweth & Stephan (2011), ch. 2, on the loyalty-shift mechanism and the
 * consistent finding that violent flanks depress both participation breadth and security
 * force defection; Martin (2007), *Justice Ignited*, on the conditions under which
 * repression backfires — outrage requires a victim the audience reads as innocent.
 * The specific cutoff is our reading of that literature, not a measured value.
 */
export const DISCIPLINE_FAILURE_BELOW = 0.6;

// ---------------------------------------------------------------------------
// Tunables — no source exists; exposed in the UI
// ---------------------------------------------------------------------------

/**
 * Softness of the join decision. At 0 a member joins the instant perceived participation
 * crosses their threshold; higher values blur the crossing, standing in for the fact that
 * nobody actually observes a national participation figure with any precision.
 * UNSOURCED — tunable.
 */
const THRESHOLD_TEMPERATURE = 0.06;

/**
 * Standard deviation of the noise on perceived participation before network density damps
 * it. Rumour, wishful thinking, and bad crowd estimates.
 * UNSOURCED — tunable.
 */
const PERCEPTION_NOISE_SD = 0.04;

/**
 * How strongly repression raises the effective threshold to join. Scaled per sector by
 * (1 - riskTolerance): the same police response reads very differently to a tenured civil
 * servant and to an undocumented delivery rider.
 * UNSOURCED — tunable. The *direction* is well established (Sharp 1973 on the costs of
 * open noncooperation); the magnitude is not measurable from campaign data.
 */
const REPRESSION_THRESHOLD_WEIGHT = 0.5;

/**
 * Fraction of the gap to target participation closed per day when joining. Deliberately
 * slower than leaving: mobilising takes meetings, leaving takes one missed rent payment.
 * UNSOURCED — tunable.
 */
const JOIN_RATE = 0.35;

/**
 * Fraction of the gap closed per day when leaving.
 * UNSOURCED — tunable.
 */
const LEAVE_RATE = 0.55;

/**
 * Strike-days of financial buffer recovered per day back at work. Well under 1: savings
 * drain faster than they refill, so a movement that stops and restarts is weaker on the
 * second attempt than it was on the first.
 * UNSOURCED — tunable.
 */
const BUFFER_RECOVERY_RATE = 0.25;

/**
 * Weight given to one's own sector versus the visible national picture. People generalise
 * from their own workplace first, but not to the exclusion of everything else — set too
 * high, a single struck sector reads its own picket line as a national uprising and
 * sustains itself indefinitely with the rest of the country at work, which is not a thing
 * that happens.
 * UNSOURCED — tunable.
 */
const OWN_SECTOR_PERCEPTION_WEIGHT = 0.35;

/**
 * Most additional private support a fully repressive response can win for a movement that
 * holds nonviolent discipline — the backfire channel. Repression suppresses *public*
 * participation and raises *private* support at the same time, and that divergence is
 * what widens the gap and loads the spring for a cascade. Below
 * `DISCIPLINE_FAILURE_BELOW` the sign flips: the same repression reads as public order
 * and builds support for the regime instead.
 * UNSOURCED — tunable. Mechanism from Martin (2007); magnitude is ours.
 */
const REPRESSION_BACKFIRE_WEIGHT = 0.22;

/**
 * Most additional private support a visibly succeeding campaign can win on momentum
 * alone. Bounded, because a large part of any population is not persuadable by a
 * bandwagon — modelling opinion as an unbounded contagion sends every run to unanimity,
 * which is both wrong and boring.
 * UNSOURCED — tunable.
 */
const BANDWAGON_PULL = 0.16;

/**
 * Share of the distance to its target that private opinion covers per day. Private
 * support is modelled as relaxing toward a level set by conditions rather than drifting
 * freely, so it returns to the underlying grievance when the campaign goes quiet instead
 * of ratcheting upward forever.
 * UNSOURCED — tunable.
 */
const SUPPORT_ADJUST_RATE = 0.06;

/**
 * How much private support is destroyed by essential-goods shortages at full severity.
 * The public's patience is a resource the movement spends, and this is the rate.
 * UNSOURCED — tunable.
 */
const SHORTAGE_PENALTY = 0.35;

/**
 * Daily pillar loyalty lost at maximum breadth of participation.
 * UNSOURCED — tunable. Breadth being the dominant term is well supported (Chenoweth &
 * Stephan 2011 on loyalty shifts); the daily rate is ours.
 */
const BREADTH_EROSION_RATE = 0.022;

/**
 * Daily pillar loyalty lost by a fully exposed pillar at total economic shutdown.
 * UNSOURCED — tunable.
 */
const ECONOMIC_EROSION_RATE = 0.03;

/**
 * Loyalty lost by a fully sensitive pillar on a day repression backfires.
 * UNSOURCED — tunable. A single outrage moves institutions much further than a day of
 * ordinary pressure does, which is why this dwarfs the rates above.
 */
const BACKFIRE_EROSION_RATE = 0.09;

/**
 * Daily pillar loyalty *recovered* when the movement's discipline has completely failed.
 * UNSOURCED — tunable. The direction is well evidenced and deliberately implemented: a
 * violent flank hands the regime's institutions a reason to close ranks.
 */
const VIOLENCE_LOYALTY_RESTORE_RATE = 0.05;

/**
 * How hard each defected pillar pulls the remaining ones after it. This term is what turns
 * defection into a cascade rather than a series of independent decisions.
 * UNSOURCED — tunable.
 */
const DEFECTION_CASCADE_RATE = 0.035;

/**
 * Daily share of lost pillar loyalty recovered when the campaign is applying no pressure.
 *
 * This is the term that lets a regime survive. Without it, erosion is permanent, every
 * campaign that outlives its own horizon eventually wins by attrition, and the simulator
 * becomes the piece of advocacy it is specifically built not to be.
 * UNSOURCED — tunable.
 */
const LOYALTY_RECOVERY_RATE = 0.045;

/**
 * Daily share of the distance to its strain-adjusted target that nonviolent discipline
 * covers, in either direction.
 *
 * Discipline is modelled as a resource an organisation actively maintains, not a fixed
 * property — which is why organisational capacity defends it, and why it recovers when
 * the pressure comes off rather than draining permanently.
 * UNSOURCED — tunable.
 */
const DISCIPLINE_EROSION_RATE = 0.05;

export const DEFAULT_TUNABLES: Tunables = {
  thresholdTemperature: THRESHOLD_TEMPERATURE,
  perceptionNoiseSd: PERCEPTION_NOISE_SD,
  repressionThresholdWeight: REPRESSION_THRESHOLD_WEIGHT,
  joinRate: JOIN_RATE,
  leaveRate: LEAVE_RATE,
  bufferRecoveryRate: BUFFER_RECOVERY_RATE,
  ownSectorPerceptionWeight: OWN_SECTOR_PERCEPTION_WEIGHT,
  repressionBackfireWeight: REPRESSION_BACKFIRE_WEIGHT,
  bandwagonPull: BANDWAGON_PULL,
  supportAdjustRate: SUPPORT_ADJUST_RATE,
  shortagePenalty: SHORTAGE_PENALTY,
  breadthErosionRate: BREADTH_EROSION_RATE,
  economicErosionRate: ECONOMIC_EROSION_RATE,
  backfireErosionRate: BACKFIRE_EROSION_RATE,
  violenceLoyaltyRestoreRate: VIOLENCE_LOYALTY_RESTORE_RATE,
  defectionCascadeRate: DEFECTION_CASCADE_RATE,
  loyaltyRecoveryRate: LOYALTY_RECOVERY_RATE,
  disciplineErosionRate: DISCIPLINE_EROSION_RATE,
};

/** UI metadata for the tunables, so the setup screen can build itself from this file. */
export interface TunableMeta {
  label: string;
  min: number;
  max: number;
  step: number;
  description: string;
}

export const TUNABLE_META: Record<keyof Tunables, TunableMeta> = {
  thresholdTemperature: {
    label: 'Threshold softness',
    min: 0.01,
    max: 0.25,
    step: 0.01,
    description: 'How sharply people act on their own participation threshold. Low values make cascades abrupt.',
  },
  perceptionNoiseSd: {
    label: 'Perception noise',
    min: 0,
    max: 0.15,
    step: 0.005,
    description: 'How badly people misjudge how many others are out.',
  },
  repressionThresholdWeight: {
    label: 'Cost of repression',
    min: 0,
    max: 1.5,
    step: 0.05,
    description: 'How much repression raises the bar for joining publicly.',
  },
  joinRate: {
    label: 'Join speed',
    min: 0.05,
    max: 1,
    step: 0.05,
    description: 'How quickly people who have decided to join actually walk out.',
  },
  leaveRate: {
    label: 'Leave speed',
    min: 0.05,
    max: 1,
    step: 0.05,
    description: 'How quickly people who have decided to stop return to work.',
  },
  bufferRecoveryRate: {
    label: 'Savings recovery',
    min: 0,
    max: 1,
    step: 0.05,
    description: 'Days of financial buffer regained per day back at work.',
  },
  ownSectorPerceptionWeight: {
    label: 'Local bias',
    min: 0,
    max: 1,
    step: 0.05,
    description: 'How much people judge the campaign by their own workplace rather than the country.',
  },
  repressionBackfireWeight: {
    label: 'Backfire strength',
    min: 0,
    max: 0.6,
    step: 0.01,
    description: 'How many bystanders a crackdown on a disciplined movement converts into supporters.',
  },
  bandwagonPull: {
    label: 'Bandwagon pull',
    min: 0,
    max: 0.5,
    step: 0.01,
    description: 'How many more people privately back a campaign that is visibly succeeding.',
  },
  supportAdjustRate: {
    label: 'Opinion speed',
    min: 0.01,
    max: 0.3,
    step: 0.01,
    description: 'How fast private opinion moves toward where conditions are pushing it.',
  },
  shortagePenalty: {
    label: 'Patience for shortages',
    min: 0,
    max: 0.8,
    step: 0.02,
    description: 'How much support the movement loses when the shelves empty. Both sides are on a clock.',
  },
  breadthErosionRate: {
    label: 'Breadth pressure',
    min: 0,
    max: 0.08,
    step: 0.002,
    description: 'How fast a campaign spanning many sectors erodes the loyalty of the institutions holding the regime up.',
  },
  economicErosionRate: {
    label: 'Economic pressure',
    min: 0,
    max: 0.1,
    step: 0.005,
    description: 'How fast economic damage erodes the loyalty of the pillars that are paying for it.',
  },
  backfireErosionRate: {
    label: 'Outrage impact',
    min: 0,
    max: 0.25,
    step: 0.005,
    description: 'How much institutional loyalty a single act of repression costs when it backfires.',
  },
  violenceLoyaltyRestoreRate: {
    label: 'Violence dividend',
    min: 0,
    max: 0.15,
    step: 0.005,
    description: "How much loyalty the regime wins back when the movement's discipline fails.",
  },
  defectionCascadeRate: {
    label: 'Cascade strength',
    min: 0,
    max: 0.12,
    step: 0.005,
    description: 'How hard each institution that walks away pulls the next one after it.',
  },
  loyaltyRecoveryRate: {
    label: 'Institutional inertia',
    min: 0,
    max: 0.15,
    step: 0.005,
    description: 'How fast wavering institutions settle back into obedience once the pressure comes off.',
  },
  disciplineErosionRate: {
    label: 'Discipline volatility',
    min: 0.005,
    max: 0.2,
    step: 0.005,
    description: "How fast the movement's commitment to nonviolence responds to pressure — in both directions.",
  },
};

export function resolveTunables(overrides: Partial<Tunables> | undefined): Tunables {
  if (!overrides) return { ...DEFAULT_TUNABLES };
  const out = { ...DEFAULT_TUNABLES };
  for (const key of Object.keys(DEFAULT_TUNABLES) as (keyof Tunables)[]) {
    const value = overrides[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}
